import React, { useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import "overlayscrollbars/overlayscrollbars.css";

import { ScopeType } from "sockapi/scope-types";
import { TeamPuzzleSockAPIScopeState } from "sockapi/scope-specs/team-spec";
import { PuzzleContentType } from "sockapi/scope-specs/puzzle-content-spec";
import { useNavStore } from "stores/NavStore";
import { useShouldDisplayAdmin } from "stores/ServerInteractionStore";
import {
  useScopeStateResolvable,
  useTeamScopeState,
  useIsHuntOver,
} from "stores/ScopeStateStore";
import { Spinner } from "components/Loading";
import { useShowSolution } from "components/PuzzlePage/PuzzlePageToolbar";
import PuzzleContentDisplay from "components/PuzzleContent/PuzzleContentDisplay";
import "./SolutionBodyDisplay.css";

function SolutionBodyContentDisplay(props: { puzName: string }) {
  const { puzName } = props;
  const solutionContent = useScopeStateResolvable(ScopeType.SOLUTION_CONTENT, {
    puzName,
  });

  if (solutionContent === null) return <Spinner inline={true} />;

  const { answer, authors, otherCredits } = solutionContent;

  const authorsStr: string | undefined = (() => {
    if (authors === undefined || authors.length === 0) return undefined;
    switch (authors.length) {
      case 1:
        return authors[0];
      case 2:
        return `${authors[0]} and ${authors[1]}`;
      default:
        return authors
          .map((author, i) =>
            i === authors.length - 1 ? `and ${author}` : author
          )
          .join(", ");
    }
  })();

  return (
    <>
      {authorsStr !== undefined ? (
        <div className="solution-authors">
          Written by {authorsStr}
          {otherCredits !== undefined
            ? otherCredits.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  <br />
                  {line}
                </React.Fragment>
              ))
            : null}
        </div>
      ) : null}
      {answer !== null ? (
        <div className="solution-answer">
          <b>Answer:</b>{" "}
          <div className="solved-title-answer spoiler">{answer}</div>
        </div>
      ) : null}
      <PuzzleContentDisplay
        puzName={puzName}
        contentType={PuzzleContentType.SOLUTION}
      />
    </>
  );
}

function useIsSolutionLocked(puzName: string) {
  const isHuntOver = useIsHuntOver();
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const teamScopeState = useTeamScopeState();
  if (isHuntOver) return false;
  if (shouldDisplayAdmin) return false;
  if (teamScopeState === null) return null;
  return teamScopeState.lockedSolutions[puzName] ?? false;
}

export default function SolutionBodyDisplay() {
  const puzData = useOutletContext<TeamPuzzleSockAPIScopeState>();
  const { puzName, slug } = puzData;
  const showSolution = useShowSolution(puzData);
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const isSolutionLocked = useIsSolutionLocked(puzName);

  useEffect(() => {
    if (showSolution ?? true) return;
    setRedirectRoute(
      slug ? { route: "puzzle", params: { slug } } : { route: "puzzles" }
    );
  }, [showSolution, slug, setRedirectRoute]);

  if (!(showSolution ?? false)) return <Spinner inline={true} />;

  if (isSolutionLocked ?? false)
    return (
      <div className="puzzle-page-body solution-page">
        <div className="locked-puzzle-content">
          This solution is available once you’ve solved the rest of the puzzles
          in the round.
        </div>
      </div>
    );

  return (
    <div className="puzzle-page-body solution-page">
      <SolutionBodyContentDisplay puzName={puzName} />
    </div>
  );
}
